<template>
  <div>
    <!-- Search Filters Section -->
    <q-expansion-item
      label="Search Filters"
      color="primary"
      switch-toggle-side
      expand-separator
      class="q-mb-md"
      :dense="true"
      expand-icon="expand_more"
      expand-icon-class="text-primary"
    >
      <div class="q-pa-md">
        <div class="row q-col-gutter-md q-mb-md">
          <!-- Customer Dropdown -->
          <div class="col-4">
            <q-select
              v-model="filters.customer_id"
              :options="customerOptions"
              label="Customer"
              outlined
              dense
            />
          </div>

          <!-- Producer Dropdown -->
          <div class="col-4">
            <q-select
              v-model="filters.producer_id"
              :options="producerOptions"
              label="Producer"
              outlined
              dense
            />
          </div>

          <!-- Driver Dropdown -->
          <div class="col-4">
            <q-select
              v-model="filters.driver_id"
              :options="driverOptions"
              label="Driver"
              outlined
              dense
            />
          </div>
        </div>

        <div class="row q-col-gutter-md q-mb-md">
          <!-- Ticket Number Input -->
          <div class="col-4">
            <q-input
              v-model="filters.ticket_number"
              label="Ticket Number"
              outlined
              dense
            />
          </div>

          <!-- Crop Dropdown -->
          <div class="col-4">
            <q-select
              v-model="filters.crop_id"
              :options="cropOptions"
              label="Crop"
              outlined
              dense
            />
          </div>

          <!-- Date Range Picker -->
          <div class="col-4">
            <div class="clickable-text">
              <q-icon
                name="event"
                size="md"
                color="primary"
                class="cursor-pointer"
              />
              <span>Select Date Range</span>
            </div>
            <q-popup-proxy
              v-model="showFilterDatePicker"
              transition-show="scale"
              transition-hide="scale"
            >
              <q-date
                v-model="filters.dateRange"
                range
                :options="restrictDates"
                mask="YYYY-MM-DD"
                class="q-pa-md"
              >
                <div class="row items-center justify-end q-gutter-sm">
                  <q-btn
                    label="Clear"
                    color="primary"
                    @click="filters.dateRange = null"
                  />
                </div>
              </q-date>
            </q-popup-proxy>
          </div>
        </div>

        <!-- Apply & Reset Filters Buttons -->
        <q-btn
          label="Apply Filters"
          color="primary"
          @click="fetchDeliveryTickets"
          class="q-mt-md"
        />
        <q-btn
          label="Reset"
          color="primary"
          flat
          class="q-ml-sm"
          @click="clearFilters"
        />
      </div>
    </q-expansion-item>

    <!-- Top Row with "Add Delivery Ticket" and Mass Edit/Delete Buttons -->
    <div class="q-mt-md">
      <div class="row-reverse">
        <!-- Single "Add" button remains -->
        <q-btn
          color="primary"
          label="Add Delivery Ticket"
          @click="onAdd"
          class="q-mb-md q-ml-lg"
        />

        <!-- Mass Edit / Mass Delete Buttons -->
        <q-btn
          color="primary"
          label="Edit Selected"
          @click="openBulkEditModal"
          class="q-mb-md q-ml-lg"
          :disable="selectedRows.length === 0"
          :class="{ 'q-opacity-50': selectedRows.length === 0 }"
        />
        <q-btn
          color="red"
          label="Delete Selected"
          @click="openBulkDeleteModal"
          class="q-mb-md q-ml-lg"
          :disable="selectedRows.length === 0"
          :class="{ 'q-opacity-50': selectedRows.length === 0 }"
        />
      </div>
    </div>

    <!-- Data Table -->
    <BaseTable
      :rows="formattedDeliveryTickets"
      :columns="columns"
      :total-rows="totalDeliveryTickets"
      search-placeholder="Search Current Table..."
    >
      <!-- "actions" slot: Checkboxes + Single-row Edit/Delete -->
      <template #actions="{ row }">
        <q-td>
          <q-checkbox
            class="q-mr-sm"
            v-model="selectedRows"
            :val="row.id"
            dense
          />
          <q-btn
            size="xs"
            color="primary"
            icon="edit"
            @click="onEdit(row)"
            class="q-mr-sm"
          />
          <q-btn
            size="xs"
            color="negative"
            icon="delete"
            @click="openDeleteDialog(row.id)"
          />
        </q-td>
      </template>
    </BaseTable>

    <!-- Pagination Control (bottom of table) -->
    <div class="row justify-left q-my-md q-ml-lg">
      <q-pagination
        v-model="currentPage"
        :max="totalPages"
        @update:model-value="onPageChange"
        color="primary"
        direction-links
        :max-pages="10"
      />
    </div>

    <!-- Single-Row Delete Confirmation Dialog -->
    <q-dialog v-model="showDeleteDialog">
      <q-card>
        <q-card-section class="text-h6">
          Confirm Deletion
        </q-card-section>

        <q-card-section>
          Are you sure you want to delete this ticket? This action cannot be undone.
        </q-card-section>

        <q-card-actions align="right">
          <!-- Cancel Button: Just close the dialog -->
          <q-btn
            flat
            label="Cancel"
            color="primary"
            v-close-popup
          />
          <!-- Confirm Button: Calls deleteItem() -->
          <q-btn
            flat
            label="Delete"
            color="negative"
            @click="deleteItem"
            v-close-popup
          />
        </q-card-actions>
      </q-card>
    </q-dialog>

    <!-- Bulk Edit Modal -->
    <q-dialog v-model="showBulkEditModal">
      <q-card class="q-pa-md">
        <q-card-section>
          <div class="text-h6">Editing {{ selectedRows.length }} Ticket(s)</div>
        </q-card-section>

        <q-form ref="editFormRef" @submit.prevent="submitEditForm">
          <q-card-section>
            <!-- Customer -->
            <q-select
              v-model="editForm.customer_id"
              :options="customerOptions"
              map-options
              option-value="value"
              option-label="label"
              label="Customer"
              outlined
              dense
            />
            <!-- Producer -->
            <q-select
              v-model="editForm.producer_id"
              :options="producerOptions"
              map-options
              option-value="value"
              option-label="label"
              label="Producer"
              outlined
              dense
              class="q-mt-md"
            />
            <!-- Driver -->
            <q-select
              v-model="editForm.driver_id"
              :options="driverOptions"
              map-options
              option-value="value"
              option-label="label"
              label="Driver"
              outlined
              dense
              class="q-mt-md"
            />
            <!-- Crop -->
            <q-select
              v-model="editForm.crop_id"
              :options="cropOptions"
              map-options
              option-value="value"
              option-label="label"
              label="Crop"
              outlined
              dense
              class="q-mt-md"
            />
            <!-- Moisture -->
            <q-input
              v-model.number="editForm.moisture"
              label="Moisture (%)"
              :rules="[validateNumeric]"
              outlined
              dense
              class="q-mt-md"
            />
            <!-- At Moisture -->
            <q-input
              v-model.number="editForm.at_moisture"
              label="At Moisture (%)"
              :rules="[validateNumeric]"
              outlined
              dense
              class="q-mt-md"
            />
            <!-- Customer Rate -->
            <q-input
              v-model.number="editForm.customer_rate"
              label="Customer Rate ($ per ton)"
              :rules="[validateNumeric]"
              outlined
              dense
              class="q-mt-md"
            />
          </q-card-section>

          <q-card-actions align="right">
            <q-btn
              label="Cancel"
              color="negative"
              @click="showBulkEditModal = false"
              flat
            />
            <q-btn
              label="Submit"
              type="submit"
              color="primary"
            />
          </q-card-actions>
        </q-form>
      </q-card>
    </q-dialog>

    <!-- Bulk Delete Confirmation Dialog -->
    <q-dialog v-model="bulkDeleteModalVisible">
      <q-card>
        <q-card-section>
          <div class="text-h6">Confirm Deletion</div>
        </q-card-section>

        <q-card-section>
          <p>Are you sure you want to delete <b>{{ selectedRows.length }}</b> ticket(s)? This action cannot be undone.</p>
        </q-card-section>

        <q-card-actions align="right">
          <q-btn flat label="Cancel" color="primary" @click="cancelBulkDelete" />
          <q-btn flat label="Delete" color="negative" @click="confirmBulkDelete" />
        </q-card-actions>
      </q-card>
    </q-dialog>

  </div>
</template>

<script>
import axios from 'axios'
import { watch } from 'vue'
import BaseTable from '@/components/BaseTable'
import { selectedYear } from '@/store/store'

export default {
  name: 'DeliveryTicketTable',
  components: {
    BaseTable
  },
  data() {
    return {
      // Ticket data
      deliveryTickets: [],
      totalDeliveryTickets: 0,
      totalPages: 1,
      currentPage: 1,
      pageSize: 25,

      // Filters
      showFilterDatePicker: false,
      filters: {
        ticket_number: null,
        dateRange: null,
        customer_id: null,
        producer_id: null,
        driver_id: null,
        crop_id: null
      },

      // Options for selects
      customerOptions: [],
      producerOptions: [],
      driverOptions: [],
      cropOptions: [],

      // Single-row delete
      showDeleteDialog: false,
      deleteId: null,

      // Bulk edit/delete
      selectedRows: [],
      showBulkEditModal: false,
      bulkDeleteModalVisible: false,

      editForm: {
        customer_id: null,
        producer_id: null,
        driver_id: null,
        crop_id: null,
        moisture: null,
        at_moisture: null,
        customer_rate: null,
        ids: []
      },

      columns: [
        {
          name: 'actions',
          align: 'center',
          label: 'Actions',
          field: 'actions',
          sortable: false
        },
        {
          name: 'ticket_number',
          align: 'left',
          label: 'Ticket Number',
          field: 'ticket_number',
          sortable: true
        },
        {
          name: 'date',
          align: 'left',
          label: 'Date',
          field: 'date',
          sortable: true
        },
        {
          name: 'customer_id',
          align: 'left',
          label: 'Customer',
          field: 'customer_id',
          sortable: true
        },
        {
          name: 'producer_id',
          align: 'left',
          label: 'Producer',
          field: 'producer_id',
          sortable: true
        },
        {
          name: 'driver_id',
          align: 'left',
          label: 'Driver',
          field: 'driver_id',
          sortable: true
        },
        {
          name: 'crop_id',
          align: 'left',
          label: 'Crop',
          field: 'crop_id',
          sortable: true
        },
        {
          name: 'gross_weight',
          align: 'left',
          label: 'Gross Weight',
          field: 'gross_weight',
          sortable: true
        },
        {
          name: 'tare_weight',
          align: 'left',
          label: 'Tare Weight',
          field: 'tare_weight',
          sortable: true
        },
        {
          name: 'customer_rate',
          align: 'left',
          label: 'Customer Rate',
          field: 'customer_rate',
          sortable: true
        }
      ]
    }
  },
  setup() {
    // For restricting dates to the selectedYear
    const restrictDates = (date) => {
      const year = selectedYear.value
      const start = `${year}/01/01`
      const end = `${year}/12/31`
      return date >= start && date <= end
    }

    return {
      restrictDates
    }
  },
  computed: {
    formattedDeliveryTickets() {
      return this.deliveryTickets.map(ticket => ({
        ...ticket,
        customer_id: ticket.customer_id?.name || 'N/A',
        producer_id: ticket.producer_id?.name || 'N/A',
        driver_id: ticket.driver_id?.name || 'N/A',
        crop_id: ticket.crop_id?.crop || 'N/A',
        date: new Date(ticket.date).toLocaleDateString()
      }))
    }
  },
  created() {
    this.fetchDeliveryTickets()
    this.fetchFilterOptions()

    watch(
      () => selectedYear.value,
      () => {
        this.currentPage = 1
        this.fetchDeliveryTickets()
      }
    )
  },
  methods: {
    async fetchFilterOptions() {
      try {
        const [customers, producers, drivers, crops] = await Promise.all([
          axios.get('/api/v1/customer/customers/'),
          axios.get('/api/v1/producer/producers/'),
          axios.get('/api/v1/driver/drivers/'),
          axios.get('/api/v1/crop/crops/')
        ])

        this.customerOptions = customers.data.map(item => ({
          label: item.name, value: item.id
        }))
        this.producerOptions = producers.data.map(item => ({
          label: item.name, value: item.id
        }))
        this.driverOptions = drivers.data.map(item => ({
          label: item.name, value: item.id
        }))
        this.cropOptions = crops.data.map(item => ({
          label: item.crop, value: item.id
        }))
      } catch (error) {
        console.error('Error fetching select options:', error)
      }
    },

    async fetchDeliveryTickets() {
      try {
        const params = {
          year: selectedYear.value,
          page: this.currentPage,
          page_size: this.pageSize,
          ...(this.filters.ticket_number && { ticket_number: this.filters.ticket_number }),
          ...(this.filters.dateRange && {
            date_start: this.filters.dateRange.from,
            date_end: this.filters.dateRange.to
          }),
          ...(this.filters.customer_id && { customer_id: this.filters.customer_id.value }),
          ...(this.filters.producer_id && { producer_id: this.filters.producer_id.value }),
          ...(this.filters.driver_id && { driver_id: this.filters.driver_id.value }),
          ...(this.filters.crop_id && { crop_id: this.filters.crop_id.value }),
        }

        const response = await axios.get(
          '/api/v1/ticket_delivery/ticket-deliveries/',
          { params }
        )

        this.deliveryTickets = response.data.results
        this.totalDeliveryTickets = response.data.count
        this.totalPages = response.data.total_pages
      } catch (error) {
        console.error('Error fetching tickets:', error)
      }
    },

    // Pagination changed => fetch
    onPageChange(page) {
      this.currentPage = page
      this.fetchDeliveryTickets()
    },

    // Single Add & Edit
    onAdd() {
      this.$router.push('/delivery_ticket/add')
    },
    onEdit(row) {
      this.$router.push(`/delivery_ticket/edit/${row.id}`)
    },

    // Single-row delete
    openDeleteDialog(id) {
      this.deleteId = id
      this.showDeleteDialog = true
    },
    async deleteItem() {
      try {
        await axios.delete(`/api/v1/ticket_delivery/ticket-deliveries/${this.deleteId}/`)
        this.fetchDeliveryTickets()
      } catch (error) {
        console.error('Error deleting ticket:', error)
      } finally {
        this.deleteId = null
      }
    },

    // Bulk Edit
    openBulkEditModal() {
      if (!this.selectedRows.length) return

      // Show modal
      this.showBulkEditModal = true

      // Populate the edit form from the first selected record
      const firstSelected = this.deliveryTickets.find(
        item => item.id === this.selectedRows[0]
      )
      if (!firstSelected) return

      this.editForm = {
        customer_id: firstSelected.customer_id?.id || null,
        producer_id: firstSelected.producer_id?.id || null,
        driver_id: firstSelected.driver_id?.id || null,
        crop_id: firstSelected.crop_id?.id || null,
        moisture: firstSelected.moisture ?? null,
        at_moisture: firstSelected.at_moisture ?? null,
        customer_rate: firstSelected.customer_rate ?? null,
        ids: []
      }
    },

    validateNumeric(value) {
      return (
        (value !== null && value !== '' && !isNaN(value)) ||
        'This field must be a numeric value'
      )
    },

    async submitEditForm() {
      this.$refs.editFormRef.validate().then(async (success) => {
        if (!success) return

        try {
          this.editForm.ids = this.selectedRows

          const editSubmitData = { ...this.editForm }

          if (editSubmitData.driver_id.label) {
            editSubmitData.driver_id = editSubmitData.driver_id.value;
          }

          if (editSubmitData.customer_id.label) {
            editSubmitData.customer_id = editSubmitData.customer_id.value;
          }

          if (editSubmitData.producer_id.label) {
            editSubmitData.producer_id = editSubmitData.producer_id.value;
          }

          if (editSubmitData.crop_id.label) {
            editSubmitData.crop_id = editSubmitData.crop_id.value;
          }

          await axios.put(
            '/api/v1/ticket_delivery/ticket-deliveries/bulk-update/',
            editSubmitData
          )

          this.$q.notify({
            color: 'green',
            message: 'Ticket(s) updated successfully!',
            icon: 'check'
          })
          this.showBulkEditModal = false
          this.fetchDeliveryTickets()
        } catch (error) {
          console.error('Error updating tickets:', error)
          this.$q.notify({
            color: 'red',
            message: 'Failed to update tickets.',
            icon: 'error'
          })
        }
      })
    },

    // Bulk Delete
    openBulkDeleteModal() {
      if (!this.selectedRows.length) {
        this.$q.notify({
          color: 'red',
          message: 'No tickets selected to delete.',
          icon: 'error'
        })
        return
      }
      this.bulkDeleteModalVisible = true
    },
    async confirmBulkDelete() {
      try {
        await axios.delete('/api/v1/ticket_delivery/ticket-deliveries/bulk-delete/', {
          data: {
            ids: this.selectedRows
          }
        })
        this.$q.notify({
          color: 'green',
          message: 'Ticket(s) deleted successfully!',
          icon: 'check'
        })
        this.fetchDeliveryTickets()
        this.selectedRows = []
      } catch (error) {
        console.error('Error deleting tickets:', error)
        this.$q.notify({
          color: 'red',
          message: 'Failed to delete tickets.',
          icon: 'error'
        })
      } finally {
        this.bulkDeleteModalVisible = false
      }
    },
    cancelBulkDelete() {
      this.bulkDeleteModalVisible = false
    },

    // Clear filters
    clearFilters() {
      this.filters = {
        ticket_number: null,
        dateRange: null,
        customer_id: null,
        producer_id: null,
        driver_id: null,
        crop_id: null
      }
      this.currentPage = 1
      this.fetchDeliveryTickets()
    }
  }
}
</script>

<style scoped>
/* Your scoped CSS (if needed) */
</style>
